import { useState, useRef, useEffect } from "react";
import {
  LogOut,
  ChevronDown,
  UserCog,
  HelpCircle,
  Moon,
  Sun,
} from "lucide-react";
import { useAuth } from "@/providers/AuthProvider";
import { useTheme } from "@/providers/ThemeProvider";
import { Link } from "react-router-dom";
import { Box, Button, Card, Text } from "@radix-ui/themes";

export function ProfileHeader() {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { currentUser, currentTenant, signOut } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSignOut = async () => {
    try {
      await signOut();
      setIsOpen(false);
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  return (
    <Box position="relative" ref={dropdownRef}>
      <Button
        variant="ghost"
        onClick={() => setIsOpen(!isOpen)}
        style={{
          padding: "var(--space-2)",
          display: "flex",
          alignItems: "center",
          gap: "var(--space-2)",
          height: "auto",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <Box style={{ textAlign: "left" }}>
          <Text size="2" weight="medium">
            {currentUser?.first_name} {currentUser?.last_name}
          </Text>
          {currentTenant && (
            <Text
              size="1"
              color="gray"
              style={{ display: "block", lineHeight: "1.2" }}
            >
              {currentTenant.name}
            </Text>
          )}
        </Box>
        <ChevronDown size={16} />
      </Button>

      {isOpen && (
        <Card
          style={{
            position: "absolute",
            right: 0,
            marginTop: "var(--space-2)",
            width: "224px",
            zIndex: 100,
            padding: 0,
          }}
        >
          <Box style={{ padding: "var(--space-2)" }}>
            <Button
              variant="ghost"
              asChild
              color="gray"
              style={{
                width: "100%",
                justifyContent: "flex-start",
                padding: "var(--space-3) var(--space-4)",
                gap: "var(--space-2)",
              }}
            >
              <Link to={`/org/${currentTenant?.id}/my-account`}>
                <UserCog size={16} />
                <Text size="2" color="gray">
                  Account Settings
                </Text>
              </Link>
            </Button>
            <Button
              variant="ghost"
              asChild
              color="gray"
              style={{
                width: "100%",
                justifyContent: "flex-start",
                padding: "var(--space-3) var(--space-4)",
                gap: "var(--space-2)",
              }}
            >
              <Link to={`/org/${currentTenant?.id}/help`}>
                <HelpCircle size={16} />
                <Text size="2" color="gray">
                  Help & Support
                </Text>
              </Link>
            </Button>
            <Button
              variant="ghost"
              color="gray"
              onClick={toggleTheme}
              style={{
                width: "100%",
                justifyContent: "flex-start",
                padding: "var(--space-3) var(--space-4)",
                gap: "var(--space-2)",
              }}
            >
              {isDarkMode ? <Sun size={16} /> : <Moon size={16} />}
              <Text size="2" color="gray">
                {isDarkMode ? "Light Mode" : "Dark Mode"}
              </Text>
            </Button>
          </Box>

          <Box
            style={{
              borderTop: "1px solid var(--gray-6)",
              padding: "var(--space-2)",
            }}
          >
            <Button
              variant="ghost"
              color="red"
              onClick={handleSignOut}
              style={{
                width: "100%",
                justifyContent: "flex-start",
                padding: "var(--space-3) var(--space-4)",
                gap: "var(--space-2)",
              }}
            >
              <LogOut size={16} />
              <Text size="2">Sign Out</Text>
            </Button>
          </Box>
        </Card>
      )}
    </Box>
  );
}
